import { Injectable, inject } from "@angular/core";
import { Router, NavigationExtras } from "@angular/router";

export const AppRoutes = {
  orders: {
    list: "/order/list",
    view: (id: number | string) => `/order/view/${id}`,
  },
  loans: {
    list: "/loan/list",
    view: (id: number | string) => `/loan/view/${id}`,
  },
  clients: {
    list: "/catalog/client/list",
    view: (id: number | string) => `/catalog/client/view/${id}`,
    edit: (id: number | string) => `/catalog/client/edit/${id}`,
    create: "/catalog/client/create",
    draft: (id: number | string) => `/catalog/client/draft/${id}`,
  },
  vendors: {
    list: "/catalog/vendor/list",
    view: (id: number | string) => `/catalog/vendor/view/${id}`,
    edit: (id: number | string) => `/catalog/vendor/edit/${id}`,
    create: "/catalog/vendor/create",
    draft: (id: number | string) => `/catalog/vendor/draft/${id}`,
  },
  tasks: {
    list: "/task/list",
    view: (id: number | string) => `/task/view/${id}`,
  },
  reports: {
    slaCompliance: "/reports/sla-compliance",
    volumesRevenue: "/reports/volumes-revenue",
    inventoryReconciliation: "/reports/inventory-reconciliation",
  },
  auth: {
    login: "/auth/login",
  },
} as const;

@Injectable({ providedIn: "root" })
export class NavigationService {
  private readonly router = inject(Router);

  navigateTo(path: string, extras?: NavigationExtras): Promise<boolean> {
    if (extras?.queryParams) {
      const queryString = new URLSearchParams(
        extras.queryParams as Record<string, string>,
      ).toString();
      const fullUrl = queryString ? `${path}?${queryString}` : path;
      return this.router.navigateByUrl(fullUrl);
    }
    return this.router.navigateByUrl(path);
  }

  // Orders
  toOrderList(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.orders.list, extras);
  }

  toOrderDetails(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.orders.view(id), extras);
  }

  // Loans
  toLoanList(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.loans.list, extras);
  }

  toLoanDetails(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.loans.view(id), extras);
  }

  // Clients
  toClientList(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.clients.list, extras);
  }

  toClientDetails(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.clients.view(id), extras);
  }

  toClientEdit(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.clients.edit(id), extras);
  }

  toClientCreate(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.clients.create, extras);
  }

  toClientDraft(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.clients.draft(id), extras);
  }

  // Vendors
  toVendorList(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.vendors.list, extras);
  }

  toVendorDetails(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.vendors.view(id), extras);
  }

  toVendorEdit(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.vendors.edit(id), extras);
  }

  toVendorCreate(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.vendors.create, extras);
  }

  toVendorDraft(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.vendors.draft(id), extras);
  }

  // Tasks
  toTaskList(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.tasks.list, extras);
  }

  toTaskDetails(
    id: number | string,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.navigateTo(AppRoutes.tasks.view(id), extras);
  }

  // Auth
  toLogin(extras?: NavigationExtras): Promise<boolean> {
    return this.navigateTo(AppRoutes.auth.login, extras);
  }

  goBack(fallbackRoute: string = "/"): void {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.navigateTo(fallbackRoute);
    }
  }

  get currentUrl(): string {
    return this.router.url;
  }
}
